"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

// Modal Context
const ModalContext = createContext(null);

// Modal Provider for managing multiple modals
const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState({});
  const t = useTranslationDetector();

  const openModal = useCallback((id, config = {}) => {
    // OPTIMIZED: Promise yerine sync state update
    setModals((prev) => ({
      ...prev,
      [id]: {
        isOpen: true,
        resolve: null,
        reject: null,
        ...config,
      },
    }));

    // Promise sadece gerekli durumlarda döndür
    return Promise.resolve();
  }, []);

  const closeModal = useCallback((id, result = null, isRejection = false) => {
    setModals((prev) => {
      const modal = prev[id];
      if (modal) {
        // Promise'i resolve/reject et
        if (isRejection && modal.reject) {
          modal.reject(result);
        } else if (modal.resolve) {
          modal.resolve(result);
        }
      }

      return {
        ...prev,
        [id]: {
          ...modal,
          isOpen: false,
          resolve: null,
          reject: null,
        },
      };
    });
  }, []);

  const confirmModal = useCallback(
    (id, result = true) => {
      closeModal(id, result, false);
    },
    [closeModal],
  );

  const cancelModal = useCallback(
    (id, result = null) => {
      closeModal(id, result, true);
    },
    [closeModal],
  );

  const getModal = useCallback(
    (id) => {
      return modals[id] || { isOpen: false };
    },
    [modals],
  );

  const setModalLoading = useCallback((id, loading) => {
    setModals((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        loading,
      },
    }));
  }, []);

  // Value objesini useMemo ile cache'le - gereksiz re-render'ları önle
  const value = useMemo(
    () => ({
      modals,
      setModals,
      openModal,
      closeModal,
      confirmModal,
      cancelModal,
      getModal,
      setModalLoading,
      t,
    }),
    [
      modals,
      setModals,
      openModal,
      closeModal,
      confirmModal,
      cancelModal,
      getModal,
      setModalLoading,
      t,
    ],
  );

  // Helper modal'ları otomatik render et - Modal component'ini import et
  const helperModals = useMemo(() => {
    // Modal component'ini lazy import etmek için dynamic import kullanacağız
    // Circular dependency'yi önlemek için
    return Object.entries(modals)
      .filter(
        ([_id, modal]) => (modal.message || modal.children) && modal.isOpen,
      )
      .map(([id]) => {
        // Dynamic import ile Modal component'ini yükle
        const Modal = require("@/components/ui/Modal").Modal;
        return <Modal key={id} id={id} />;
      });
  }, [modals]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {helperModals}
    </ModalContext.Provider>
  );
};

// Hook for accessing modal context
const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};

// Hook for modal management using context
const useModal = (id) => {
  const {
    openModal,
    closeModal,
    confirmModal,
    cancelModal,
    getModal,
    setModalLoading,
  } = useModalContext();

  const modal = getModal(id);

  const open = useCallback(
    (config = {}) => {
      return openModal(id, config);
    },
    [id, openModal],
  );

  const close = useCallback(
    (result = null) => {
      closeModal(id, result, false);
    },
    [id, closeModal],
  );

  const confirm = useCallback(
    (result = true) => {
      confirmModal(id, result);
    },
    [id, confirmModal],
  );

  const cancel = useCallback(
    (result = null) => {
      cancelModal(id, result);
    },
    [id, cancelModal],
  );

  const toggle = useCallback(
    (config = {}) => {
      if (modal.isOpen) {
        close();
      } else {
        return open(config);
      }
    },
    [modal.isOpen, open, close],
  );

  const setLoading = useCallback(
    (loading) => {
      setModalLoading(id, loading);
    },
    [id, setModalLoading],
  );

  return {
    isOpen: modal.isOpen,
    open,
    close,
    confirm,
    cancel,
    toggle,
    setLoading, // Expose setLoading
    loading: modal.loading || false, // Expose loading state
    config: modal,
  };
};

// Temizlenmiş helper methods - tek config parametresi ile
const createModalHelpers = (modalContext) => {
  const { openModal, setModalLoading, closeModal, setModals, t } = modalContext;

  const confirmModal = (config = {}) => {
    const id = `confirm-${Date.now()}`;

    return new Promise((resolve) => {
      const defaultConfig = {
        title: t("common.confirmTitle") || "Onaylıyor musunuz?",
        size: "sm",
        showCloseButton: true,
        closeOnBackdropClick: true,
        showCancel: true,
        ...config,
        // Özel flag ekle - bu bir async confirm modal'ı
        isAsyncConfirm: true,
      };

      // Modal state'ini manuel olarak ayarla
      setModals((prev) => ({
        ...prev,
        [id]: {
          isOpen: true,
          ...defaultConfig,
          // Özel resolve fonksiyonu - modal'ı KAPATMADAN resolve eder
          resolveWithoutClosing: () => {
            resolve({
              confirmed: true,
              setLoading: (loading) => setModalLoading(id, loading),
              close: () => closeModal(id),
            });
          },
          // Normal reject - modal kapanır
          reject: () => {
            closeModal(id);
            resolve({
              confirmed: false,
              setLoading: () => {},
              close: () => {},
            });
          },
        },
      }));
    });
  };

  const successModal = async (config = {}) => {
    const id = `success-${Date.now()}`;
    const defaultConfig = {
      title: t("common.defaultSuccessTitle"),
      variant: "success",
      size: "sm",
      showCancel: false,
      showCloseButton: true,
      closeOnBackdropClick: true,
      ...config,
    };

    try {
      await openModal(id, defaultConfig);
      return {
        id,
        close: () => closeModal(id),
        setLoading: (loading) => setModalLoading(id, loading),
      };
    } catch {
      return {
        id,
        close: () => {},
        setLoading: () => {},
      };
    }
  };

  const errorModal = async (config = {}) => {
    const id = `error-${Date.now()}`;
    const defaultConfig = {
      variant: "error",
      size: "sm",
      showCancel: false,
      showCloseButton: true,
      closeOnBackdropClick: true,
      ...config,
      title: "",
    };

    try {
      await openModal(id, defaultConfig);
      return {
        id,
        close: () => closeModal(id),
        setLoading: (loading) => setModalLoading(id, loading),
      };
    } catch {
      return {
        id,
        close: () => {},
        setLoading: () => {},
      };
    }
  };

  const warningModal = async (config = {}) => {
    const id = `warning-${Date.now()}`;
    const defaultConfig = {
      title: t("common.defaultWarningTitle"),
      variant: "warning",
      size: "sm",
      showCancel: false,
      showCloseButton: true,
      closeOnBackdropClick: true,
      ...config,
    };

    try {
      await openModal(id, defaultConfig);
      return {
        id,
        close: () => closeModal(id),
        setLoading: (loading) => setModalLoading(id, loading),
      };
    } catch {
      return {
        id,
        close: () => {},
        setLoading: () => {},
      };
    }
  };

  const infoModal = async (config = {}) => {
    const id = `info-${Date.now()}`;
    const defaultConfig = {
      title: t("common.defaultInfoTitle"),
      variant: "info",
      size: "sm",
      showCancel: false,
      showCloseButton: true,
      closeOnBackdropClick: true,
      ...config,
    };

    try {
      await openModal(id, defaultConfig);
      return {
        id,
        close: () => closeModal(id),
        setLoading: (loading) => setModalLoading(id, loading),
      };
    } catch {
      return {
        id,
        close: () => {},
        setLoading: () => {},
      };
    }
  };

  return { confirmModal, successModal, errorModal, warningModal, infoModal };
};

// Global modal helpers hook - destructuring destekli
const useModalHelpers = () => {
  const context = useModalContext();
  const helpers = createModalHelpers(context);

  return {
    ...helpers,
    // Context method'larını da expose et
    setModalLoading: context.setModalLoading,
    closeModal: context.closeModal,
    modals: context.modals,
  };
};

export {
  ModalContext,
  ModalProvider,
  useModal,
  useModalContext,
  useModalHelpers,
};
